import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PhoneVerify from "@/components/PhoneVerify";

const GUEST_EMAIL_RX = /@guest\.thespotters\.in$/i;

export default function MyAccount() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  if (!user) return null;

  // Passwordless / guest accounts start with a placeholder email — let them add a
  // real one (no password required) so order updates arrive by mail.
  const needsEmail = !user.email || GUEST_EMAIL_RX.test(user.email);

  const saveEmail = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { toast.error("Enter a valid email"); return; }
    setSavingEmail(true);
    try {
      const { data } = await api.post("/auth/complete-account", { email: email.trim() });
      setUser({ ...user, ...data });
      setEmail("");
      toast.success("Email saved — order updates will arrive in your inbox");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save email");
    } finally { setSavingEmail(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name };
      if (phoneVerified) payload.phone = phone;
      const { data } = await api.patch("/auth/profile", payload);
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-12 py-12">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Your Account</p>
      <h1 className="font-serif text-6xl tracking-tighter">My Account.</h1>

      <div className="mt-10 border border-foreground p-6 md:p-8 space-y-7 max-w-xl">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Full Name</p>
          <input value={name} onChange={(e) => setName(e.target.value)} className="underline-input" />
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Email</p>
          {needsEmail ? (
            <>
              <div className="flex gap-2 items-center">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                  className="underline-input flex-1" autoComplete="email" />
                <button onClick={saveEmail} disabled={savingEmail}
                  className="border border-foreground px-3 py-2 text-[10px] font-mono uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-40 whitespace-nowrap">
                  {savingEmail ? "Saving…" : "Save"}
                </button>
              </div>
              <p className="font-mono text-[10px] text-muted-foreground mt-2">Add your email to get order &amp; delivery updates by mail. No password needed.</p>
            </>
          ) : (
            <p className="font-serif text-lg break-all">{user.email}</p>
          )}
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Mobile Number</p>
          <PhoneVerify phone={phone} onPhoneChange={setPhone} onVerified={setPhoneVerified} />
          <p className="font-mono text-[10px] text-muted-foreground mt-2">Verify to save this number and use it at checkout.</p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Account Type</p>
          <p className="font-serif text-lg capitalize">{user.role}</p>
        </div>
        <button onClick={save} disabled={saving}
          className="bg-foreground text-background px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
